/**
 * Dev-only "View As" debugging utility.
 *
 * Lets a developer impersonate another user (and optionally a set of Okta
 * groups) on the LOCAL DEV server, so visibility/permission bugs can be
 * reproduced without re-deploying. The server only honors these headers when
 * NODE_ENV !== 'production' AND DEBUG_VIEW_AS=1 is set on the server process,
 * so this is safe to ship.
 *
 * Usage in any fetch call:
 *   import { viewAsHeaders } from './debug/viewAs';
 *   fetch(url, { headers: { ...viewAsHeaders() } });
 */

const STORAGE_KEY = 'lyric-debug-view-as';

// Gate on an explicit build-time flag, NOT NODE_ENV. CRA forces NODE_ENV='production'
// during `npm run build`, which would compile the widget out even when we want it
// available in a local prod build for debugging. Set REACT_APP_DEBUG_VIEW_AS=1 at
// build time to include the widget. Never set this in the deployed/CI build.
export const VIEW_AS_ENABLED = process.env.REACT_APP_DEBUG_VIEW_AS === '1';

export function getViewAs() {
  if (!VIEW_AS_ENABLED) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.email) return null;
    return {
      email: String(parsed.email),
      groups: Array.isArray(parsed.groups) ? parsed.groups : [],
      label: parsed.label || parsed.email,
    };
  } catch {
    return null;
  }
}

export function setViewAs(value) {
  if (!VIEW_AS_ENABLED) return;
  if (!value) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  }
  // Notify listeners (the widget + any subscribed components) in the same tab.
  window.dispatchEvent(new CustomEvent('lyric-view-as-changed', { detail: value || null }));
}

export function viewAsHeaders() {
  const v = getViewAs();
  if (!v) return {};
  const headers = { 'X-Debug-View-As-Email': v.email };
  if (v.groups.length) headers['X-Debug-View-As-Groups'] = v.groups.join(',');
  return headers;
}

/**
 * Preset identities for the widget dropdown. Edit freely — these only ever
 * leave your machine as request headers to your own dev server.
 */
export const VIEW_AS_PRESETS = [
  { label: 'Engineering teammate (everyone)', email: 'teammate@rapidai.com', groups: ['everyone'] },
  { label: 'Engineering teammate (engineering)', email: 'teammate@rapidai.com', groups: ['engineering', 'everyone'] },
  { label: 'PM (no engineering group)', email: 'pm@rapidai.com', groups: ['product', 'everyone'] },
  { label: 'External user (no groups)', email: 'external@example.com', groups: [] },
];
